import { stripEditorBackground } from '@/utils/interior/editableHtml';
import { localize } from './localize';

/**
 * 매장 OPEN 품의(233) — 공사비·투자 적정성 리포트 분석 결과 매핑.
 * 레거시 appr_edit_forms_open_construction_cost 의 mergeAiResult·mapAiResult 를 그대로 옮김.
 * 저장 attributes = { aiPipelineId, content(리포트 HTML 스냅샷), data(분석 결과 원문 배열) }.
 */

export const OPEN_CONSTRUCTION_COST_TYPE = 233;
/** 인테리어 OPEN 비용 정산(234) — 233 과 같은 진입·업로더·attributes, 리포트 화면만 다름 */
export const OPEN_CONSTRUCTION_COST_SETTLEMENT_TYPE = 234;

/** 233·234 공통 — 폼 상태(openConstructionCost)·저장 attributes·필수 검사를 같이 씀 */
export const isOpenConstructionCostType = (apvType: number): boolean =>
  apvType === OPEN_CONSTRUCTION_COST_TYPE ||
  apvType === OPEN_CONSTRUCTION_COST_SETTLEMENT_TYPE;

export const isOpenConstructionCostSettlementType = (
  apvType: number,
): boolean => apvType === OPEN_CONSTRUCTION_COST_SETTLEMENT_TYPE;

/** 인테리어 보고서 → 작성 화면 진입 쿼리 키 — 레거시 `create?apv_type=233&analysisId=` 와 동일 */
export const OPEN_CONSTRUCTION_COST_ENTRY_QUERY_KEY = 'analysisId';

export const getEntryAnalysisId = (
  query: Record<string, string | string[] | undefined>,
): string | undefined => {
  const raw = query[OPEN_CONSTRUCTION_COST_ENTRY_QUERY_KEY];
  const value = (Array.isArray(raw) ? raw[0] : raw)?.trim();
  return value || undefined;
};

export type OpenConstructionCostState = {
  /** 업로드 분석은 AI 파이프라인 id, 보고서 진입은 보고서 id(레거시 VC001 과 같은 값) */
  aiPipelineId: string;
  /** 분석 결과 원문 — 업로드는 display.results, 보고서 진입은 보고서 조회 응답 1건 */
  data: unknown[];
};

export type OpenConstructionCostAttributes = {
  aiPipelineId: string;
  content: string;
  data: unknown[];
};

type Rec = Record<string, any>;

const isBlank = (v: unknown) => v === null || v === undefined || v === '';

const asRecord = (v: unknown): Rec | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Rec) : null;

export const hasOpenConstructionCostAnalysis = (
  state: OpenConstructionCostState | null | undefined,
): state is OpenConstructionCostState =>
  !!state?.aiPipelineId && state.data.length > 0;

/** 레거시와 같이 임시저장·상신 모두 분석 결과 필수(서버도 data 비면 422) */
export const checkOpenConstructionCostBeforeSave = (
  state: OpenConstructionCostState | null | undefined,
): string | null =>
  hasOpenConstructionCostAnalysis(state)
    ? null
    : '파일을 업로드하여 AI 분석을 먼저 진행해 주세요.';

/** 저장 data 는 레거시 문서에 객체 1건(보고서 진입분)으로도 남아 있어 배열로 맞춤 */
export const parseOpenConstructionCostAttributes = (
  attributes: unknown,
): OpenConstructionCostState | null => {
  const a = asRecord(attributes);
  if (!a) return null;
  const raw = a.data;
  const data = Array.isArray(raw) ? raw : asRecord(raw) ? [raw] : [];
  if (!data.length) return null;
  const aiPipelineId = String(a.aiPipelineId ?? a.analysisId ?? '');
  return { aiPipelineId, data };
};

// ===== 레거시 스크립트 이관 =====

/** 상태값 → 뱃지 색상 클래스 */
export const statusTagClass = (status?: string): string => {
  switch ((status || '').trim()) {
    case '적정':
    case '적합':
    case '양호':
    case '확보':
    case '적정성 확보':
    case '우수':
    case '달성':
      return 'is-success';
    case '매우우수':
      return 'is-blue';
    case '보통':
      return 'is-default';
    case '주의':
    case '조건부':
    case '미흡':
    case '지연':
      return 'is-warning';
    case '높음':
    case '초과':
    case '부적정':
    case '부적합':
    case '재검토':
    case '개선필요':
    case '미달':
      return 'is-caution';
    default:
      return 'is-default';
  }
};

/** 증감 텍스트(+/-) → 값 색상 클래스. positiveIsGood 이면 + 가 초록 */
export const signColorClass = (
  text: string | undefined,
  positiveIsGood: boolean,
): string => {
  const t = (text || '').trim();
  if (!t || !/\d/.test(t)) return '';
  const negative = t.charAt(0) === '-';
  const good = positiveIsGood ? !negative : negative;
  return good ? 'val-success' : 'val-caution';
};

export const normalizeBrandName = (brandName?: string): string =>
  brandName
    ? brandName
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/[^a-z0-9]/g, '')
    : '';

/** 파이프라인이 결과를 여러 원소로 나눠 주는 경우 값이 있는 쪽을 살림 */
export const mergeAiResult = (values: unknown): Rec | null => {
  const list = Array.isArray(values) ? values : [values];
  let merged: Rec | null = null;
  list.forEach((it) => {
    const o = it && (it as Rec).result ? (it as Rec).result : it;
    if (!o || typeof o !== 'object') return;
    if (!merged) merged = {};
    const target: Rec = merged;
    Object.keys(o).forEach((k) => {
      const val = (o as Rec)[k];
      if (isBlank(val)) return;
      const cur = target[k];
      if (cur === undefined) {
        target[k] =
          val && typeof val === 'object' && !Array.isArray(val)
            ? { ...val }
            : val;
      } else if (Array.isArray(val)) {
        if (!Array.isArray(cur) || val.length > cur.length) target[k] = val;
      } else if (
        typeof val === 'object' &&
        typeof cur === 'object' &&
        !Array.isArray(cur)
      ) {
        Object.keys(val).forEach((k2) => {
          if (isBlank(cur[k2])) cur[k2] = val[k2];
        });
      }
    });
  });
  return merged;
};

export type OpenCostScenario = {
  label: string;
  value: string;
  highlight?: boolean;
};

export type OpenCostBasis = {
  opinionHTML: string;
  status: string;
  rateStatus: string;
  period: string;
  subcaption: string;
  profitLabel: string;
  monthlyProfit: string;
  profitRate: string;
  scenarios: OpenCostScenario[];
};

export type OpenCostBudgetRow = {
  type: string;
  amount: string;
  storePrice: string;
  benchmarkPrice: string;
  deviation: string;
  note: string;
};

export type OpenConstructionCostView = {
  benchmarkType: string;
  project: {
    brand: string;
    projectName: string;
    channel: string;
    area: string;
    period: string;
    openDate: string;
    expectedCost: string;
    constructionType: string;
  };
  overallOpinionHTML: string;
  salesProfit: {
    target: string;
    targetMonthly: string;
    lastYear: string;
    lastYearMonthly: string;
    lastYearLabel: string;
    operatingProfit: string;
    operatingProfitRate: string;
    directProfit: string;
    directProfitRate: string;
  };
  basis: { operating: OpenCostBasis; direct: OpenCostBasis };
  compare: {
    status: string;
    statusVariant: string;
    caption: string;
    deviationText: string;
    benchmarkPrice: string;
    storePrice: string;
    totalCost: string;
    designGrade: string;
    designStatus: string;
    deviationPercent: string;
    tolerance: string;
    reason: string;
    tag: string;
    note: string;
    reasonLine: string;
  };
  roi: {
    status: string;
    rateStatus: string;
    period: string;
    monthlyProfit: string;
    profitRate: string;
    accumulated: string;
    plannedPeriod: string;
    scenarios: OpenCostScenario[];
  };
  budgetSummary: OpenCostBudgetRow[];
  specialNotesHTML: string;
};

const comma = (n: unknown): string =>
  isBlank(n) ? '' : String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const UNIT_KEYS: Record<string, string> = {
  원: '{{amount}}원',
  '원/월': '{{amount}}원/월',
  '원/평': '{{amount}}원/평',
};
export const unit = (n: unknown, u?: string) => {
  if (isBlank(n)) return '';
  const key = u ? UNIT_KEYS[u] : undefined;
  return key ? localize(key, { amount: comma(n) }) : comma(n) + (u || '');
};
const monthText = (n: unknown, u?: string) =>
  !u || u === '개월' ? localize('{{n}}개월', { n }) : `${n}${u}`;
const months = (n: unknown, u?: string) =>
  n === null || n === undefined ? '' : monthText(n, u);
const signedUnit = (n: unknown, u?: string): string => {
  if (isBlank(n)) return '';
  if (typeof n === 'string' && /[^0-9+\-.,\s]/.test(n)) return n.trim();
  return (Number(n) > 0 ? '+' : '') + unit(n, u || '원');
};
const pick = (...args: unknown[]): any => {
  for (let i = 0; i < args.length; i += 1) {
    if (!isBlank(args[i])) return args[i];
  }
  return '';
};
const rate = (n: unknown, u?: string) =>
  isBlank(n) ? '' : localize('이익률 {{rate}}', { rate: `${n}${u || '%'}` });
const pctVal = (n: unknown, u?: string) =>
  isBlank(n) ? '' : `${n}${u || '%'}`;
const monthsOf = (n: unknown, u?: string) =>
  isBlank(n) ? '' : monthText(n, u);

/** 비교군 대비 = 본매장 평단가 − 비교군 평단가 (응답에 금액 필드가 있으면 우선) */
const deviationAmount = (c: Rec): string => {
  const given = pick(c.deviationAmount, c.deviationPerPY);
  if (given !== '') return signedUnit(given, c.deviationAmountUnit || '원');
  const store = c.unitCostPerPY;
  const bench = c.benchmarkUnitCostPerPY;
  const hasStore = !isBlank(store);
  const hasBench = !isBlank(bench);
  if (!hasStore && !hasBench) return '';
  return signedUnit(
    (hasStore ? Number(store) : 0) - (hasBench ? Number(bench) : 0),
    '원',
  );
};

/** YYMM("2607") → "2026-07", 그 외 포맷은 그대로 */
const formatOpenDate = (d: unknown): string => {
  const t = d === null || d === undefined ? '' : String(d).trim();
  return /^\d{4}$/.test(t) ? `20${t.slice(0, 2)}-${t.slice(2)}` : t;
};

/** 1개월 미만은 주 단위(0.5 → "2주") — 포탈 보고서 표기 규칙과 동일 */
const periodLabel = (m: unknown): string => {
  const n = Number(m);
  if (m === '' || Number.isNaN(n) || n <= 0) return '';
  if (n >= 1) return localize('{{n}}개월', { n });
  const w = Math.round(n * 4);
  return Math.abs(n * 4 - w) < 0.01
    ? localize('{{n}}주', { n: w })
    : localize('{{n}}개월', { n });
};

/** aiSummary 가 기준별 객체({operating, direct})로 오는 경우까지. strict 면 기준 전용 문장만 */
const summaryText = (
  src: unknown,
  prefer: RegExp,
  strict?: boolean,
): string => {
  if (typeof src === 'string') return strict ? '' : stripEditorBackground(src);
  const o = asRecord(src);
  if (!o) return '';
  const keys = Object.keys(o);
  for (let i = 0; i < keys.length; i += 1) {
    if (prefer.test(keys[i]) && typeof o[keys[i]] === 'string')
      return stripEditorBackground(o[keys[i]] as string);
  }
  if (strict) return '';
  for (let i = 0; i < keys.length; i += 1) {
    if (typeof o[keys[i]] === 'string')
      return stripEditorBackground(o[keys[i]] as string);
  }
  return '';
};

const monthlyAvg = (annual: unknown) =>
  annual === '' || Number.isNaN(Number(annual))
    ? ''
    : Math.round(Number(annual) / 12);

const RX = {
  op: /operat|영업/i,
  dr: /direct|직접/i,
  profit: /(profit|income|이익)/i,
  rate: /(rate|margin|pct|률)/i,
  sales: /(sales|revenue|매출)/i,
  target: /(target|goal)/i,
  prev: /(prev|last|py|전년)/i,
  month: /(month|monthly|월)/i,
  recover: /(recover|payback|회수)/i,
  plan: /(plan|planned|계획|deprec)/i,
  span: /(month|period|기간)/i,
};

const flattenPaths = (
  obj: unknown,
  prefix: string,
  out: Record<string, unknown>,
  depth: number,
): Record<string, unknown> => {
  if (!obj || typeof obj !== 'object' || depth > 4) return out;
  Object.keys(obj).forEach((k) => {
    const val = (obj as Rec)[k];
    const path = prefix ? `${prefix}.${k}` : k;
    if (val && typeof val === 'object') flattenPaths(val, path, out, depth + 1);
    else if (!isBlank(val)) out[path] = val;
  });
  return out;
};

const scenarioSet = (r: Rec): OpenCostScenario[] => [
  {
    label: localize('보수 (-20%)'),
    value: months(r.recoveryMonthsPessimistic, r.recoveryMonthsPessimisticUnit),
  },
  {
    label: localize('기준 (+0%)'),
    value: months(r.recoveryMonthsBase, r.recoveryMonthsBaseUnit),
    highlight: true,
  },
  {
    label: localize('낙관 (+20%)'),
    value: months(r.recoveryMonthsOptimistic, r.recoveryMonthsOptimisticUnit),
  },
];

/** AI 분석 결과(응답 스키마 여러 벌) → 화면 표시값 */
export const mapAiResult = (
  values: unknown,
): OpenConstructionCostView | null => {
  const r = mergeAiResult(values);
  if (!r) return null;
  const v: Rec = r.value || {};
  const cs: Rec = r.costStatus || {};
  const roi: Rec = r.roiData || {};
  const rows: Rec[] = Array.isArray(r.costComparisonData)
    ? r.costComparisonData
    : [];
  const sp: Rec = r.salesPnlSummary || r.salesProfitData || r.salesProfit || {};
  const roiOp: Rec = roi.operating || r.roiDataOperating || {};
  const roiDr: Rec = roi.direct || r.roiDataDirect || roi;
  const roiCm: Rec = roi.common || {};
  const pnl: Rec = r.pnl || sp.pnl || {};
  const pnlRaw: Rec = pnl.raw_inputs || pnl.rawInputs || {};

  // 응답 스키마가 여러 벌이라 지정 이름으로 못 찾으면 경로 키워드로 탐색(공사비 요약 행 제외)
  const flatPaths = flattenPaths(r, '', {}, 0);
  const seek = (include: RegExp[], exclude?: RegExp[], asText?: boolean) => {
    const keys = Object.keys(flatPaths);
    for (let i = 0; i < keys.length; i += 1) {
      const path = keys[i];
      const val = flatPaths[path];
      if (/costComparisonData/i.test(path)) continue;
      if (asText) {
        if (typeof val !== 'string' || val.length < 10) continue;
      } else if (Number.isNaN(Number(val))) continue;
      let ok = include.every((rx) => rx.test(path));
      if (ok && exclude) ok = !exclude.some((rx) => rx.test(path));
      if (ok) return val;
    }
    return '';
  };

  const deviationPercent: string = cs.deviationPercent || '';
  const devToken = (deviationPercent.match(/[+-]?\d+(\.\d+)?%/) || [''])[0];
  const devLabel = deviationPercent
    .replace(/\s*[+-]?\d+(\.\d+)?%.*$/, '')
    .trim();
  const summaryAll = `${summaryText(v.aiSummary, RX.op) || ''} ${
    summaryText(v.aiSummary, RX.dr) || ''
  }`;
  const plannedMonths =
    (summaryAll.match(/계획\s*운영\s*기간\s*\(?\s*(\d+(?:\.\d+)?)\s*개월/) ||
      [])[1] || '';
  const targetAnnual = pick(
    sp.salesTargetAnnual,
    sp.targetSales,
    v.targetSales,
    pnl.sales_target_annual_won,
    pnl.salesTargetAnnualWon,
    seek([RX.sales, RX.target], [RX.month, RX.prev]),
  );
  const lastYearAnnual = pick(
    sp.prevYearSalesAnnual,
    sp.lastYearSales,
    v.lastYearSales,
    pnl.prev_year_sales_annual_won,
    pnl.prevYearSalesAnnualWon,
    seek([RX.sales, RX.prev], [RX.month]),
  );
  // 팝업(운영기간 기준) 문서 판별은 포탈 저장분 basis 플래그만(채널명·문안 추정 금지)
  const periodBasis =
    pick(sp.salesTargetBasis, pnl.sales_target_basis) === 'operating_period';
  const periodMonths = pick(
    sp.salesTargetPeriodMonths,
    pnl.sales_target_period_months,
  );
  const periodTotal = pick(
    sp.salesTargetPeriodWon,
    pnl.sales_target_period_won,
  );
  const tol = String(v.designGradeAllowedPct || '').replace(/^허용\s*/, '');

  const salesProfit = (() => {
    const targetMonthlyVal = pick(
      sp.salesTargetMonthlyAvg,
      sp.targetSalesMonthly,
      sp.targetSalesMonthlyAvg,
      v.targetSalesMonthly,
      pnl.sales_target_monthly_won,
      monthlyAvg(targetAnnual),
    );
    const lastYearMonthlyVal = pick(
      sp.prevYearSalesMonthlyAvg,
      sp.lastYearSalesMonthly,
      sp.lastYearSalesMonthlyAvg,
      v.lastYearSalesMonthly,
      pnlRaw.actual_sales_won,
      pnlRaw.actualSalesWon,
      monthlyAvg(lastYearAnnual),
    );
    const pLabel = periodLabel(periodMonths);
    // 실제로 발생하는 금액을 크게, 환산치를 작게(포탈 보고서 §1 카드와 동일).
    // 팝업 1개월 미만만 운영기간 총액이 주 표기, 전년(직전) 실적은 늘 월평균이 주 표기
    const subMonth =
      periodBasis &&
      periodMonths !== '' &&
      Number(periodMonths) > 0 &&
      Number(periodMonths) < 1 &&
      periodTotal !== '';
    const periodSum = (won: unknown) =>
      isBlank(won)
        ? ''
        : pLabel
        ? localize('운영기간 {{period}} 합계 {{amount}}', {
            period: pLabel,
            amount: unit(won, '원'),
          })
        : localize('운영기간 합계 {{amount}}', { amount: unit(won, '원') });
    const annualSum = (won: unknown) =>
      isBlank(won)
        ? ''
        : localize('연 환산 {{amount}}', { amount: unit(won, '원') });
    const targetSub = periodBasis
      ? periodSum(periodTotal)
      : annualSum(targetAnnual);
    const lastYearSub = periodBasis
      ? periodSum(lastYearAnnual)
      : annualSum(lastYearAnnual);
    return {
      target: subMonth
        ? unit(periodTotal, '원') +
          (pLabel
            ? ` · ${localize('운영 {{period}}', { period: pLabel })}`
            : '')
        : unit(targetMonthlyVal, '원/월'),
      targetMonthly: subMonth
        ? targetMonthlyVal !== ''
          ? localize('월 환산 {{amount}}', {
              amount: unit(targetMonthlyVal, '원'),
            })
          : ''
        : targetSub,
      lastYear: unit(lastYearMonthlyVal, '원/월'),
      lastYearMonthly: lastYearSub,
      lastYearLabel: localize(periodBasis ? '직전 매출실적' : '전년 매출실적'),
      operatingProfit: unit(
        pick(
          roiOp.monthlyProfit,
          sp.monthlyOperatingProfit,
          roiOp.monthlyOperatingProfit,
          roi.monthlyOperatingProfit,
          pnl.monthly_operating_profit_won,
        ),
        pick(roiOp.monthlyProfitUnit, '원'),
      ),
      operatingProfitRate: rate(
        pick(
          roiOp.profitMarginPct,
          sp.operatingProfitMarginPct,
          roi.operatingProfitMarginPct,
          pnl.operating_profit_rate,
        ),
        roiOp.profitMarginPctUnit,
      ),
      directProfit: unit(
        pick(
          roiDr.monthlyProfit,
          roiDr.monthlyDirectProfit,
          sp.monthlyDirectProfit,
          roi.monthlyDirectProfit,
          pnl.monthly_direct_profit_won,
        ),
        pick(roiDr.monthlyProfitUnit, roiDr.monthlyDirectProfitUnit, '원'),
      ),
      directProfitRate: rate(
        pick(
          roiDr.profitMarginPct,
          sp.directProfitMarginPct,
          pnl.direct_profit_rate,
        ),
        roiDr.profitMarginPctUnit,
      ),
    };
  })();

  return {
    benchmarkType: r.benchmarkType || '',
    project: {
      brand: v.brand || '',
      projectName: v.title || '',
      channel: v.channel || '',
      area:
        v.areaInPY !== null && v.areaInPY !== undefined
          ? `${v.areaInPY}${v.areaInPYUnit ? ` ${v.areaInPYUnit}` : ''}`
          : '',
      period:
        v.startDate && v.endDate
          ? `${v.startDate} ~ ${v.endDate}`
          : v.startDate || v.endDate || '',
      openDate: formatOpenDate(v.openDate),
      expectedCost: unit(v.totalCost, v.totalCostUnit || '원'),
      constructionType: v.constructionType || '',
    },
    overallOpinionHTML: summaryText(v.aiSummary, RX.op),
    salesProfit,
    basis: {
      operating: {
        opinionHTML: pick(
          summaryText(v.aiSummary, RX.op, true),
          v.aiSummaryOperating,
          v.aiSummaryByOperatingProfit,
        ),
        status: roiOp.recoveryGrade || '',
        rateStatus: roiOp.profitMarginGrade || '',
        period: months(roiOp.recoveryMonthsBase, roiOp.recoveryMonthsBaseUnit),
        subcaption: localize('직접이익 − 영업비 기준 영업이익 회수'),
        profitLabel: localize('월 영업이익'),
        monthlyProfit: unit(
          pick(
            roiOp.monthlyProfit,
            roiOp.monthlyOperatingProfit,
            sp.monthlyOperatingProfit,
          ),
          pick(roiOp.monthlyProfitUnit, '원'),
        ),
        profitRate: pctVal(
          pick(roiOp.profitMarginPct, sp.operatingProfitMarginPct),
          roiOp.profitMarginPctUnit,
        ),
        scenarios: scenarioSet(roiOp),
      },
      direct: {
        opinionHTML: pick(
          summaryText(v.aiSummary, RX.dr),
          v.aiSummaryDirect,
          v.aiSummaryByDirectProfit,
        ),
        status: roiDr.recoveryGrade || '',
        rateStatus: roiDr.profitMarginGrade || '',
        period: months(roiDr.recoveryMonthsBase, roiDr.recoveryMonthsBaseUnit),
        subcaption: localize('매출총이익 − 직접비 기준 직접이익 회수'),
        profitLabel: localize('월 직접이익'),
        monthlyProfit: unit(
          pick(roiDr.monthlyProfit, roiDr.monthlyDirectProfit),
          pick(roiDr.monthlyProfitUnit, roiDr.monthlyDirectProfitUnit, '원'),
        ),
        profitRate: pctVal(roiDr.profitMarginPct, roiDr.profitMarginPctUnit),
        scenarios: scenarioSet(roiDr),
      },
    },
    compare: {
      status: cs.verdict || '',
      statusVariant:
        cs.verdict === '적정' || cs.verdict === '적합'
          ? 'is-success'
          : 'is-caution',
      caption: pick(cs.deviationLabel, devLabel),
      deviationText: cs.overallEvaluation || '',
      benchmarkPrice: unit(
        cs.benchmarkUnitCostPerPY,
        cs.benchmarkUnitCostPerPYUnit || '원',
      ),
      storePrice: unit(cs.unitCostPerPY, cs.unitCostPerPYUnit || '원'),
      totalCost: unit(cs.totalCost, cs.totalCostUnit || '원'),
      designGrade: v.designGrade || '',
      designStatus: v.designGradeVerdict || '',
      deviationPercent: devToken,
      tolerance: tol,
      reason: v.designGradeReason || '',
      tag: v.designGradeTag || '',
      note: v.designGradeNote || '',
      reasonLine: v.designGradeReasonLine || '',
    },
    roi: {
      status: roiDr.recoveryGrade || '',
      rateStatus: roiDr.profitMarginGrade || '',
      period: months(roiDr.recoveryMonthsBase, roiDr.recoveryMonthsBaseUnit),
      monthlyProfit: unit(
        pick(roiDr.monthlyProfit, roiDr.monthlyDirectProfit),
        pick(roiDr.monthlyProfitUnit, roiDr.monthlyDirectProfitUnit, '원'),
      ),
      profitRate: pctVal(roiDr.profitMarginPct, roiDr.profitMarginPctUnit),
      accumulated: unit(
        pick(roiDr.cumulativeProfit, roiDr.fiveYearCumulativeProfit),
        pick(
          roiDr.cumulativeProfitUnit,
          roiDr.fiveYearCumulativeProfitUnit,
          '원',
        ),
      ),
      plannedPeriod: monthsOf(
        pick(
          roiCm.planOperatingMonths,
          roi.plannedOperatingMonths,
          v.plannedOperatingMonths,
          pnl.depreciation_months,
          pnl.accounting_depr_months,
          plannedMonths,
          seek([RX.plan, RX.span], [RX.recover, RX.profit, RX.rate]),
        ),
        pick(roiCm.planOperatingMonthsUnit, roi.plannedOperatingMonthsUnit),
      ),
      scenarios: scenarioSet(roiDr),
    },
    budgetSummary: rows.map((c) => ({
      type: c.type || '',
      amount: unit(c.cost, c.costUnit || '원'),
      storePrice: unit(c.unitCostPerPY, c.unitCostPerPYUnit || '원/평'),
      benchmarkPrice:
        c.benchmarkUnitCostPerPY !== null &&
        c.benchmarkUnitCostPerPY !== undefined
          ? unit(
              c.benchmarkUnitCostPerPY,
              c.benchmarkUnitCostPerPYUnit || '원/평',
            )
          : '-',
      deviation: deviationAmount(c),
      note: c.note || '',
    })),
    // 보고서 편집 카드에서 브라우저가 붙인 배경색은 결재문서에서 음영이 된다(PORTAL-15540)
    specialNotesHTML: stripEditorBackground(v.aiAnomalyAnalysis || ''),
  };
};

export type OpenCostBasisMode = 'operating' | 'direct';

export const hasBasis = (b?: OpenCostBasis | null): b is OpenCostBasis =>
  !!(b && (b.opinionHTML || b.period || b.monthlyProfit));

/** 판정기준 초기값 — 영업이익 기준 우선, 없으면 직접이익, 둘 다 없으면 null(roi 기본값 사용) */
export const initialBasisMode = (
  view: OpenConstructionCostView,
): OpenCostBasisMode | null => {
  if (hasBasis(view.basis.operating)) return 'operating';
  if (hasBasis(view.basis.direct)) return 'direct';
  return null;
};

/** 보고서 진입 시 결과에 실려 오는 원본 HTML 리포트 파일 */
export const getReportFile = (
  data: unknown[],
): { fileUuid: string; fileName: string } | null => {
  const first = asRecord(data[0]);
  if (!first) return null;
  const result = asRecord(first.result) ?? first;
  const rf = asRecord(result.reportFile);
  if (!rf?.fileUuid) return null;
  return { fileUuid: String(rf.fileUuid), fileName: String(rf.fileName ?? '') };
};
